/**
 * Educational curtain & blind style guides — /curtains-dubai/styles/{slug}.
 * Grouped by what the term actually describes (heading, fabric/layers,
 * blind, or track & operation) so M-track is correctly hardware, not a pleat.
 * Copy is factual; no unsupported "most trending" claims, no prices.
 * `formValue` maps to the existing curtain quote form's product-type select.
 */

export const STYLE_GROUPS = [
  { id: "heading", label: "Curtain Headings & Stitching", blurb: "How the fabric is gathered and folded at the top — this defines the look of the curtain." },
  { id: "fabric", label: "Fabrics & Layers", blurb: "What the curtain is made of and how layers combine for light, softness and privacy." },
  { id: "blind", label: "Blinds", blurb: "Roller, Roman, zebra and vertical designs for different light and privacy needs — practical and precise for kitchens, offices and minimal rooms." },
  { id: "operation", label: "Track & Operation", blurb: "The hardware and drive that carries and moves the curtain." },
];

export const STYLE_GUIDES = [
  {
    slug: "ripple-fold",
    name: "Ripple Fold Curtains",
    group: "heading",
    formValue: "Ripple Fold",
    mediaKey: "style-ripple-fold",
    short: "Continuous soft S-waves that glide on a track — the clean, modern heading.",
    whatYouSee: "Uniform, rounded S-shaped waves running the full width of the window, front and back, with no visible gathering at the top.",
    construction: "The fabric hangs from evenly spaced carriers on a track using snap tape — the wave is set by the carrier spacing, not by stitching pleats into the fabric. Requires track hardware (it cannot run on a decorative rod).",
    fitting: "Works best ceiling-fixed or in a curtain recess so the track disappears and the waves read floor-to-ceiling. Needs enough stack-back space for the folded fabric when open.",
    benefits: ["Clean, architectural look suited to modern Dubai apartments", "Glides smoothly and evenly by hand or motor", "Same neat appearance from inside and outside"],
    whoFor: "Living rooms, bedrooms and full-height glazing where a calm, contemporary line matters.",
    considerations: ["Track hardware is required — confirmed at measurement", "Uses more fabric width than a flat panel", "Wave depth depends on the carrier spacing chosen"],
    related: ["pinch-pleat", "sheer", "motorized"],
  },
  {
    slug: "m-track",
    name: "M-Track Curtains",
    group: "operation",
    formValue: "M-Track",
    mediaKey: "style-m-track",
    short: "A track system — the hardware that carries the curtain, not a pleat style.",
    whatYouSee: "A slim aluminium track at the ceiling or wall with gliders that carry the curtain; the fabric heading above it can be ripple fold, pinch pleat or wave.",
    construction: "M-track refers to the track profile that the curtain runs on. The exact profile section, bend options, bracket type and cord/motor compatibility are confirmed against the physical hardware at survey — pairings differ between heading types.",
    fitting: "Ceiling-fixed, wall-fixed or recessed into a curtain pelmet box. Can be bent for bay windows depending on the profile.",
    benefits: ["Smooth, quiet glide", "Neat minimal look with no visible rod", "Can be specified hand-drawn, corded or motor-ready"],
    whoFor: "Anyone choosing a track-based curtain — the heading style is selected separately.",
    considerations: ["It is hardware, not a heading: pair it with a pleat or wave style", "Exact section and motor compatibility are confirmed with the physical hardware", "Recessed installs need pelmet depth confirmed on site"],
    related: ["ripple-fold", "motorized", "pinch-pleat"],
  },
  {
    slug: "pinch-pleat",
    name: "Pinch Pleat Curtains (Triple Pinch)",
    group: "heading",
    formValue: "Pinch Pleat",
    mediaKey: "style-pinch-pleat",
    short: "Hand-finished pleats pinched in groups of three — the classic tailored heading.",
    whatYouSee: "Fabric gathered into crisp fan-shaped pleats at the top. In a triple pinch, each pleat group is made of exactly three folds sewn together at the base, opening into a fan above.",
    construction: "Buckram-stiffened heading with pleats stitched by hand or machine at set intervals; hooks sit behind each pleat group. Uses roughly 2.2–2.5× fabric fullness for a rich drape.",
    fitting: "Hangs from a track or decorative rod. The stitched pleats hold their shape whether the curtain is open or closed.",
    benefits: ["Timeless, tailored appearance", "Structured folds that hold their form", "Suits both classic villas and formal living rooms"],
    whoFor: "Formal majlis, villas and rooms where a dressmaker-finished look is wanted.",
    considerations: ["More structured and traditional than ripple fold", "Pleat spacing is fixed once stitched", "Triple pinch uses more fabric than a two-fold pleat"],
    related: ["two-pleat", "ripple-fold", "double-layer"],
  },
  {
    slug: "two-pleat",
    name: "Two Pleat Curtains (Double Pinch)",
    group: "heading",
    formValue: "Two Pleat (Double Pinch)",
    mediaKey: "style-two-pleat",
    short: "The same tailored pinch heading, grouped in pairs of two folds — softer and more relaxed.",
    whatYouSee: "Pleat groups of exactly two folds pinched together at the base, giving a flatter, more contemporary rhythm than the fuller triple pinch.",
    construction: "Identical stitched-heading technique to the triple pinch, but each group gathers two folds instead of three, using slightly less fabric fullness (around 2×).",
    fitting: "Runs on the same tracks or rods as a triple pinch; the lighter grouping stacks back a little tighter when open.",
    benefits: ["Tailored but more relaxed than triple pinch", "Slightly lighter fabric use", "Neater stack-back on narrow walls"],
    whoFor: "Modern homes that want a stitched heading without the full formality of triple pinch.",
    considerations: ["Less full-bodied than a triple pinch — a styling choice, not a downgrade", "Grouping (two folds vs three) is the only structural difference", "Confirm fullness with your fabric weight at survey"],
    related: ["pinch-pleat", "ripple-fold", "double-layer"],
  },
  {
    slug: "double-sheer",
    name: "Double Sheer Curtains",
    group: "fabric",
    formValue: "Double Sheer",
    mediaKey: "style-double-sheer",
    short: "Two translucent layers — a heavier sheer room-side over a fine voile window-side.",
    whatYouSee: "A heavier translucent layer on the room side over a fine voile on the window side, running on two independent tracks. Daylight passes through both, casting soft layered shadows and depth.",
    construction: "Two separate sheer fabrics of different weights on two independent tracks, so each layer draws on its own.",
    fitting: "Needs a double track (or two tracks) with enough pelmet or ceiling depth for both layers.",
    benefits: ["Soft, dimensional daylight with layered shadow play", "Each layer moves independently", "An airy, couture look for living spaces"],
    whoFor: "Living and dining rooms where daytime softness and depth matter more than darkness.",
    considerations: ["Both layers are translucent — this is NOT a night-privacy or blackout solution", "For darkness or evening privacy, add an opaque or blackout layer instead", "Requires double-track depth confirmed at measurement"],
    related: ["sheer", "double-layer", "blackout"],
  },
  {
    slug: "blackout",
    name: "Blackout Curtains",
    group: "fabric",
    formValue: "Blackout",
    mediaKey: "style-blackout",
    short: "Opaque light-blocking fabric for bedrooms — installed detailing decides real darkness.",
    whatYouSee: "A dense, opaque curtain that blocks light through the fabric itself, in plain weaves, textures or with a separate blackout lining.",
    construction: "Either an inherently opaque 3-pass fabric or a face fabric with a blackout lining; any heading style can be applied.",
    fitting: "Blackout fabric does not by itself guarantee total darkness — light can enter around the edges. Ceiling-fixed tracks, generous side overlaps, returns or a pelmet reduce light gaps; we detail this at survey.",
    benefits: ["Darker, cooler bedrooms", "Helps with late sleepers and home cinemas", "Pairs with a sheer for daytime softness"],
    whoFor: "Bedrooms, nurseries and media rooms.",
    considerations: ["Total installed darkness depends on edge detailing, not fabric alone", "Consider a double layer (sheer + blackout) for day and night use", "Heavier fabric — track and fixing spec confirmed on site"],
    related: ["double-layer", "sheer", "roman"],
  },
  {
    slug: "sheer",
    name: "Sheer Curtains",
    group: "fabric",
    formValue: "Sheer",
    mediaKey: "style-sheer",
    short: "Light, translucent voiles that soften daylight and views.",
    whatYouSee: "A single translucent layer that diffuses sunlight, softens glare and gently veils the view while keeping rooms bright.",
    construction: "Fine voile, linen-look or textured sheer fabrics; usually generous fullness with a wave or pleated heading.",
    fitting: "Often floor-to-ceiling for height; commonly the inner layer of a double-track setup.",
    benefits: ["Bright, airy rooms", "Softens harsh Dubai sunlight", "Beautiful movement in air-conditioning airflow"],
    whoFor: "Living areas and any room where daylight is welcome.",
    considerations: ["Daytime softness only — little night privacy on its own", "Pair with blackout or an opaque curtain for bedrooms", "Fabric choice changes how much view is retained"],
    related: ["double-sheer", "double-layer", "ripple-fold"],
  },
  {
    slug: "double-layer",
    name: "Double Layer Curtains",
    group: "fabric",
    formValue: "Double Layer",
    mediaKey: "style-double-layer",
    short: "A sheer plus an opaque curtain on two tracks — day softness, night privacy.",
    whatYouSee: "An inner sheer for daytime light with an outer opaque or blackout curtain drawn at night — the standard bedroom combination.",
    construction: "Two independent tracks: a translucent sheer window-side and an opaque decorative or blackout curtain room-side (the opposite logic to a double sheer, which is two translucent layers).",
    fitting: "Double track, ideally ceiling-fixed or in a pelmet; each layer operates separately by hand or motor.",
    benefits: ["Day and night control in one window", "Layered, finished designer look", "Each layer can be motorized independently"],
    whoFor: "Bedrooms and living rooms that need both daylight softness and evening privacy.",
    considerations: ["Needs double-track depth", "Choose the opaque layer's fabric for the level of darkness you want", "Differs from double sheer, which stays translucent"],
    related: ["blackout", "sheer", "double-sheer"],
  },
  {
    slug: "roller",
    name: "Roller Blinds",
    group: "blind",
    formValue: "Roller Blinds",
    mediaKey: "style-roller",
    short: "A single clean panel that rolls onto a discreet tube — minimal and practical.",
    whatYouSee: "A flat fabric panel that rolls up into a slim cassette or open tube above the glass.",
    construction: "Fabric bonded to a roller tube with chain or motor drive; available in sunscreen, translucent or blackout fabrics.",
    fitting: "Inside the window recess for a flush look, or face-fixed above it; compact and easy to clean around.",
    benefits: ["Minimal footprint", "Wide fabric openness range (view vs privacy)", "Great for kitchens, studies and offices"],
    whoFor: "Modern minimal rooms and utility spaces.",
    considerations: ["Blackout roller fabrics still show edge light in a recess", "Exact recess measurements are critical", "Chain-side and control choices confirmed at survey"],
    related: ["zebra", "roman", "motorized"],
  },
  {
    slug: "zebra",
    name: "Zebra Blinds",
    group: "blind",
    formValue: "Zebra Blinds",
    mediaKey: "style-zebra",
    short: "Alternating sheer and solid bands that align for view or privacy.",
    whatYouSee: "Horizontal striped fabric where rotating the layers aligns sheer-on-sheer (open light) or solid-on-solid (privacy).",
    construction: "A continuous banded fabric looping over a roller tube — adjusting it shifts the band alignment.",
    fitting: "Like a roller blind: recess or face-fixed, chain or motor operated.",
    benefits: ["Light control without lifting the whole blind", "Contemporary layered look", "Good daytime privacy adjustment"],
    whoFor: "Living rooms, offices and bedrooms wanting adjustable daytime light.",
    considerations: ["Bands are translucent/solid — not a blackout solution on their own", "Alignment control is stepped, not infinitely variable", "Fabric band scale should suit window size"],
    related: ["roller", "vertical", "sheer"],
  },
  {
    slug: "roman",
    name: "Roman Blinds",
    group: "blind",
    formValue: "Roman Blinds",
    mediaKey: "style-roman",
    short: "Soft tailored fabric folds that stack in neat horizontal pleats.",
    whatYouSee: "A flat fabric panel that draws up into elegant horizontal folds — the softest, most curtain-like blind.",
    construction: "Fabric with horizontal rods/battens and a lift system; can be lined, interlined or blackout-lined.",
    fitting: "Recess or face-fixed; beautiful on smaller windows and in joinery-framed openings.",
    benefits: ["Tailored, soft appearance", "Uses decorator fabrics like a curtain", "Space-saving on narrow sills"],
    whoFor: "Studies, bedrooms and classic interiors.",
    considerations: ["Stacks at the top — covers part of the glass when raised", "Lining choice sets the light control", "Wide spans may need split blinds"],
    related: ["roller", "blackout", "pinch-pleat"],
  },
  {
    slug: "vertical",
    name: "Vertical Blinds",
    group: "blind",
    formValue: "Vertical Blinds",
    mediaKey: "style-vertical",
    short: "Rotating vertical louvres for wide spans and office glazing.",
    whatYouSee: "Full-height fabric louvres that rotate for light control and draw fully aside like a curtain.",
    construction: "Weighted fabric vanes on a headrail with rotation and traverse control, wand or cord operated.",
    fitting: "Ideal for very wide or sliding-door openings; face or ceiling fixed.",
    benefits: ["Covers very wide spans economically", "Rotates for fine light control", "Draws fully clear of the glass"],
    whoFor: "Offices, clinics and wide balcony doors.",
    considerations: ["More commercial in character", "Vane width should suit the window scale", "Not a blackout solution"],
    related: ["roller", "zebra", "motorized"],
  },
  {
    slug: "motorized",
    name: "Motorized Curtains & Blinds",
    group: "operation",
    formValue: "Motorized",
    mediaKey: "style-motorized",
    short: "Silent motor drive with remote, app or schedule control.",
    whatYouSee: "Curtains or blinds that glide via a remote, a wall switch, a schedule — or a phone app where the selected control system supports it — with no cords.",
    construction: "A motor drives the track or roller tube. We fit Somfy and Silent Gliss motor systems; Lutron scene integration is possible with compatible shades via the appropriate interface, and smart-home / voice compatibility depends on the selected motor and hub system — confirmed per project at specification.",
    fitting: "Needs power provision (or battery motor options) at the track — planned at survey, ideally before finishing works.",
    benefits: ["Effortless daily operation", "Schedules for light and privacy", "No cords — cleaner and safer"],
    whoFor: "Full-height glazing, tall windows, smart homes and holiday homes.",
    considerations: ["Compatibility depends on the chosen motor + hub system", "Power routing is best planned before painting", "Hand-drawn override varies by system"],
    related: ["m-track", "ripple-fold", "roller"],
  },
];

export const GUIDE_BY_SLUG = Object.fromEntries(STYLE_GUIDES.map((g) => [g.slug, g]));
